import React, { useState, useEffect } from 'react';
import {
  Modal,
  Card,
  Typography,
  Button,
  Grid,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import axios from '../../../sharedModules/utils/axios.utils';
import { SelectOption } from '../../../sharedModules/form/model';

interface ImpersonateUserModalProps {
  open: boolean;
  onClose: () => void;
}

const ImpersonateUserModal: React.FC<ImpersonateUserModalProps> = ({ open, onClose }) => {
  const [users, setUsers] = useState<SelectOption[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<SelectOption>(null);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get<any>('/admin/impersonations');
      setUsers(response.data.user_options);
      setError(null);
    } catch (err) {
      setError('Failed to fetch users. Please try again.');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchUsers();
    }
  }, [open]);

  const handleImpersonateUser = async () => {
    if (!selectedUser) return;

    try {
      setLoading(true);
      await axios.post('/admin/impersonations', { microsoft_auth_id: selectedUser.value });

      sessionStorage.setItem('impersonatedAuthId', selectedUser.value);

      window.location.reload();
    } catch (err) {
      setError('Failed to impersonate user. Please try again.');
      console.error('Error impersonating user:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="impersonate-user-modal-title">
      <Card
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '500px',
          padding: '20px',
          maxHeight: '80vh',
          overflow: 'auto',
        }}
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h5" id="impersonate-user-modal-title">
              Impersonate User
            </Typography>
          </Grid>

          {error && (
            <Grid item xs={12}>
              <Typography color="error">{error}</Typography>
            </Grid>
          )}

          <Grid item xs={12}>
            {loading ? (
              <Grid container justifyContent="center">
                <CircularProgress />
              </Grid>
            ) : (
              <FormControl fullWidth>
                <InputLabel id="select-user-label">Select User</InputLabel>
                <Select
                  labelId="select-user-label"
                  id="select-user"
                  value={selectedUser?.value || ''}
                  label="Select User"
                  onChange={(e) => {
                    const userId = e.target.value as string;
                    const user = users.find((u) => u.value === userId);
                    setSelectedUser(user);
                  }}
                >
                  {users.map((user) => (
                    <MenuItem key={user.value} value={user.value}>
                      {user.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Grid>

          <Grid item xs={12} container justifyContent="flex-end" spacing={1}>
            <Grid item>
              <Button onClick={onClose} variant="outlined">
                Cancel
              </Button>
            </Grid>
            <Grid item>
              <Button
                onClick={handleImpersonateUser}
                variant="contained"
                color="primary"
                disabled={loading || !selectedUser}
              >
                Impersonate
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Card>
    </Modal>
  );
};

export default ImpersonateUserModal;
